/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import { crc32 } from 'polycrc';
import unset from 'lodash.unset';
import has from 'lodash.has';
import get from 'lodash.get';
import set from 'lodash.set';
import _isEqual from 'lodash.isequal';
import mergeWith from 'lodash.mergewith';
import _isEmpty from 'lodash.isempty';

import { getStore, mergeDeep } from '../store/state/state-manager';
import constants from '../constants';
import FieldUtils from '../fields';
import { ActionManagerInstance } from '../actions/manager';
import AnnotationProcessor from '../annotation/processor';
import * as StateManager from '../store/state/state-manager';
import type { ComponentMetadataConfig } from '../interpreter/types';
import type { FeaturesFromInfinity } from '../environment-info/types';
import type { Basic, Nested, NestedObject, Primitive } from '../types';
import type { PropertyInfo } from '../globals';
import type { C11nEnv } from '../interpreter/c11n-env';
import { getPropertyMetadata } from '../rules/rules-store';

const INVALID_PROPERTY_CHARS = ' @#$%=!<>/\\';

/**
 * helper functions
 */
class Utils {
  /**
   * Merges all source properties to target and returns the target object
   * @param source object to be merged
   * @param target target object
   */
  static mergeDeep(source: any, target: any) {
    return mergeWith(target, source);
  }

  /**
   * Checks if refPath is a direct property of sourceObject.
   * @param sourceObject The object to query
   * @param refPath The path to check.
   * @returns Returns true if path exists, else false.
   */
  static hasIn(sourceObject: any, refPath: string | string[]): boolean {
    return has(sourceObject, refPath);
  }

  static getIn(sourceObject: any, refPath: any, defaultValue?: any): any {
    return get(sourceObject, refPath, defaultValue);
  }

  static setIn(sourceObject: any, refPath: any, objectToSet: any) {
    return set(sourceObject, refPath, objectToSet);
  }

  static deleteIn(sourceObject: any, refPath: any) {
    return unset(sourceObject, refPath);
  }

  /**
   * Checks wether obj is empty or not.
   * @param obj input obj
   */

  static isObjectEmpty(obj: object) {
    return !obj || Object.entries(obj).length === 0;
  }

  /**
   * Checks if value is an empty object, collection, map, or set.
   * @param value input value
   */

  static isEmpty(value: any) {
    return _isEmpty(value);
  }

  /**
   * Clones passed object.
   * @param obj object to clone
   */

  static deepClone(obj = {}) {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * Copies the source object to target object
   * it is a shallow copy.
   * @param source source to copy
   * @param target target
   */

  static copy(source: { [key: string]: any }, target: { [key: string]: any }) {
    Object.keys(source).forEach((key) => {
      target[key] = source[key];
    });
  }

  /**
   * Checks wether reference is valid property
   * reference or not.
   * @param ref  property string to check
   */

  static isPropertyRef(ref: string) {
    if (!ref || !ref.includes('.')) {
      return false;
    }

    for (let i = 0; i < ref.length; i += 1) {
      if (INVALID_PROPERTY_CHARS.includes(ref[i])) {
        return false;
      }
    }

    return true;
  }

  /**
   * Checks whether when is configured in the component or not.
   * @param config component metadata
   */

  static isWhenExist(config: NonNullable<ComponentMetadataConfig['config']>): boolean {
    const conditionAttributes = ['visibility', 'disabled', 'readOnly', 'required'];
    let whenExist = false;
    for (const key of Object.keys(config)) {
      const value = config[key as keyof ComponentMetadataConfig['config']];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        whenExist = whenExist || Utils.isWhenExist(value);
      } else if (
        conditionAttributes.includes(key) &&
        typeof value === 'string' &&
        (value as string)?.startsWith('@W ')
      ) {
        /* format {"type":"exp","config":{"exp":".Gender = 'Male'","props":[".Gender"]}} */
        whenExist = true;
      }
    }
    // Check for whens in embedded data CRUD operations
    if (!whenExist && config?.type === 'multirecordlist' && config?.renderMode === 'Editable') {
      whenExist = Object.keys(config?.allowActions ?? {}).some(
        (key) => typeof config?.allowActions?.[key] === 'string' && config.allowActions[key]?.startsWith('@W ')
      );
    }
    return whenExist;
  }

  /**
   * Helper function to get the When name if when is configured on the component. If when is not configured it will return empty string.
   * @param config
   * @returns all the whens configured on the field.
   */
  static getWhenNames(config?: ComponentMetadataConfig): string[] {
    const conditionAttributes = ['visibility', 'disabled', 'readOnly', 'required'];
    let value = '';
    const WhenNames: Set<string> = new Set();
    if (config) {
      Object.keys(config).forEach((key) => {
        /* format {"type":"exp","config":{"exp":".Gender = 'Male'","props":[".Gender"]}} */
        const item = config[key as keyof ComponentMetadataConfig];
        if (conditionAttributes.includes(key) && typeof item === 'string' && item.startsWith('@W ')) {
          value = AnnotationProcessor.trimAnnotation(item) as string;
          WhenNames.add(value);
        }
      });
    }
    // Check for whens in embedded data CRUD operations
    if (config?.type === 'multirecordlist' && config?.renderMode === 'Editable') {
      const allowActions = config?.allowActions ?? {};
      Object.keys(allowActions).forEach((key) => {
        if (typeof allowActions[key] === 'string' && allowActions[key]?.startsWith('@W ')) {
          value = AnnotationProcessor.trimAnnotation(allowActions[key]);
          WhenNames.add(value);
        }
      });
    }
    return [...WhenNames];
  }

  /**
   * Helper function to check whether expression is configured
   * in the component or not.
   * @param config component metadata
   */

  static isExpressionExist(config: NonNullable<ComponentMetadataConfig['config']>) {
    const conditionAttributes = ['disabled', 'visibility', 'readOnly', 'required'];
    let whenExist = false;
    for (const key of Object.keys(config)) {
      if (
        conditionAttributes.includes(key) &&
        typeof config[key as keyof ComponentMetadataConfig['config']] === 'string' &&
        (config[key as keyof ComponentMetadataConfig['config']] as string)?.startsWith('@E ')
      ) {
        whenExist = true;
      }
    }
    return whenExist;
  }

  static buildDataObject(dataObject: { [key: string]: any }, context: string) {
    let dObject: { [key: string]: any } = {};
    const rootObject = dObject;

    if (context.indexOf('.') > 0) {
      const tokens = context.split('.');
      tokens.forEach((token) => {
        if (token.includes('[')) {
          const [pageToken] = token.split('[');
          dObject[pageToken] = [];
          dObject = dObject[pageToken];
          token = token.substring(token.indexOf('[') + 1, token.lastIndexOf(']'));
        }
        dObject[token] = {};
        dObject = dObject[token];
      });
    } else {
      dObject[context] = {};
      dObject = dObject[context];
    }

    Object.keys(dataObject).forEach((key) => {
      let k = key;
      if (key.startsWith('.')) {
        k = key.substring(1);
      }
      dObject[k] = dataObject[key];
    });

    return rootObject;
  }

  /**
   * Builds form submit object. Object will be
   * in json format.
   * @param propRef property reference
   * @param resultObj json object
   * @param value value to update to property.
   */

  static buildObject = (propRef: string, resultObj: any, value: any) => {
    if (value === undefined) {
      return;
    }
    const props = propRef.split('.');
    let i = 0;
    for (; i < props.length - 1; i += 1) {
      const key = props[i];
      if (key?.toString().length && !resultObj[key]) {
        resultObj[key] = {};
      }
      if (resultObj[key]) {
        resultObj = resultObj[key];
      }
    }
    if (typeof value === 'object') {
      if (!resultObj[props[i]]) {
        resultObj[props[i]] = Array.isArray(value) ? [] : {};
      }
      mergeDeep(resultObj[props[i]], value, { isArrayDeepMerge: true });
    } else {
      resultObj[props[i]] = value;
    }
  };

  /**
   * get hashed crc32 string
   * @param value string to be hashed
   * @returns hashed string
   */

  static getHashedString(value: string): string {
    // @ts-expect-error
    return crc32(value).toString(16);
  }

  /**
   * extract the property name
   * @param value string from which name to be extracted
   * @returns returns the property name without @P
   * @example <caption>Example for getPropertyName(value)</caption>
   * getPropertyName("@P .prop1")
   * this will return "prop1"
   */

  static getPropertyName(value: string) {
    if (value) {
      const isProperty = value.startsWith('@P');
      const index = isProperty ? value.indexOf(' ') : -1;
      return isProperty ? value.substring(index + 2) : value;
    }
    return value;
  }

  /**
   * extract the property name
   * @param dependency string/object from which name to be extracted
   * @returns returns the property name
   * @example <caption>Example for getPropertyNameFromDependency(value)</caption>
   * getPropertyNameFromDependency({
      mode: 'multiRecord',
      propertyName: 'caseInfo.content.PartnerOffering.ProductAreas[0].PegaProdMulti'
    })
   * this will return "caseInfo.content.PartnerOffering.ProductAreas[0].PegaProdMulti"
   */

  static getPropertyNameFromDependency(dependency: string | { [key: string]: any }): string {
    return typeof dependency === 'string' ? dependency : dependency.propertyName;
  }

  /**
   * This method will check whether the supplied jsonstring is valid Json after parsed.
   * @param jsonstring - string which results a Valid JSON
   * @returns this will return false if the supplied string is not valid json string else return
   * the parsed JSON Object
   */
  static isValidJson(jsonstring: string): boolean | object {
    try {
      return JSON.parse(jsonstring);
    } catch (error) {
      console.error('not a Valid JSON', error);
      return false;
    }
  }

  /**
   * Compares 2 objects for equality.
   * It does deep comparison.
   * @param object1 object 1 to be compared
   * @param object2 object 2 compare against
   */
  static isEqual(object1: object, object2: object) {
    return _isEqual(object1, object2);
  }

  /**
   * Check if object1 contains object2.
   * It does deep comparison.
   * @param object1 object 1 contains object2
   * @param object2 object 2 object that needs to be checked
   */
  static isContains(object1: { [key: string]: any }, object2: { [key: string]: any }) {
    const objArray = Object.keys(object1);
    for (let i = 0; i < objArray.length; i += 1) {
      if (object1[objArray[i]] && Utils.isEqual(object1[objArray[i]], object2)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Removes property from existingDependentPropertiesSet.
   * It does deep comparison.
   * @param existingDependentPropertiesSet set containing existing properties
   * @param propertyName property name that needs to be removed
   */

  static removeExistingDependentProperty(existingDependentPropertiesSet: PropertyInfo[], propertyName: string) {
    const index = existingDependentPropertiesSet.findIndex((property) => property.propertyName === propertyName);
    if (index >= 0) {
      existingDependentPropertiesSet.splice(index, 1);
    }
  }

  /* Gives full property reference.
   * @param splitPropertyArray Array of split property
   * @returns returns full property path
   */
  static getFullPropertyReference(splitPropertyArray: string[]) {
    return Array.isArray(splitPropertyArray) ? splitPropertyArray.filter((val) => !!val).join('.') : '';
  }

  /**
   * Iterates over the object and calls the callback on reaching
   * to the leaf of object with two parameters - path and value,
   * where path value will be array such as ['root', 'branch', 'leaf'] and value can be of any type
   * @function iterateLeafNodes
   * @param sourceObj object to iterate
   * @param callback function which will be executed on reaching leaf node
   * @example <caption>Example for iterateLeafNodes</caption>
   * const data = {Name: 'James Bond', department: {id: '007', type: 'agent'}};
   * iterateLeafNodes(data, (path, value) => {
        if (path.pop() === 'Name' && value === 'James Bond') {
          sendOnMission(value);
        }
      });
   */
  static iterateLeafNodes(
    sourceObj: NestedObject,
    callback: (path: (number | string)[], value: Nested | Basic) => void
  ) {
    const iterator = (obj: Nested | Basic, path: (number | string)[] = []) => {
      Object.entries(obj).forEach(([key, value]) => {
        path.push(Array.isArray(obj) ? Number(key) : key);
        if (Array.isArray(value)) {
          value.forEach((v, i) => {
            path.push(i);
            iterator(v, path);
            path.pop();
          });
        } else if (typeof value === 'object') {
          iterator(value as NestedObject, path);
        } else {
          callback(path.slice(), value);
        }
        path.pop();
      });
    };
    iterator(sourceObj);
  }

  /**
   * Resolves string boolean values of config object properties to boolean values
   * @param configObject config props object of a component
   * @private
   * @returns configObject of a component
   */
  static resolveStringBooleans(configObject: { [key: string]: unknown }) {
    const booleanProps = ['required', 'readOnly', 'visibility'];
    const toBool = (stringBool: string) => {
      return stringBool === 'true';
    };
    Object.keys(configObject).forEach((key) => {
      if (booleanProps.includes(key) && typeof configObject[key] === 'string') {
        configObject[key] = toBool(configObject[key] as string);
      }
    });
    return configObject;
  }

  /**
   * API to generate UUID
   * @returns a unique Id
   */
  static generateUniqueID(): string {
    return crypto.randomUUID();
  }

  static unflatten(data: object) {
    const result: NestedObject = {};
    Object.entries(data).forEach(([key, value]) => {
      const tokens = key.split('.');
      let v;
      tokens.reduce<NestedObject>((accumulator, current, index) => {
        if (accumulator[current]) {
          v = accumulator[current];
        } else if (!Number.isNaN(Number(tokens[index + 1]))) {
          v = [];
        } else if (tokens.length - 1 === index) {
          v = value;
        } else {
          v = {};
        }
        accumulator[current] = v;
        return accumulator[current] as NestedObject;
      }, result);
    });
    return result;
  }

  static flatten(data: Nested | Primitive, path: string | undefined, resultObj: { [key: string]: Primitive }) {
    if (!path) {
      path = '';
    }
    if (Array.isArray(data)) {
      data.forEach((value, index) => {
        Utils.flatten(value, `${path}[${index}]`, resultObj);
      });
    } else if (typeof data === 'object' && data !== null) {
      Object.keys(data).forEach((key) => {
        const tempPath = path ? `${path}.${key}` : key;
        Utils.flatten(data[key], tempPath, resultObj);
      });
    } else {
      resultObj[path] = data;
    }
  }

  static buildInstructionPageReference(pageReference: string, property: string) {
    const isObjectInfo = pageReference.startsWith(constants.OBJECT_INFO.OBJECT_INFO);
    const target = isObjectInfo
      ? `${pageReference}${property}`.replace(constants.OBJECT_INFO.OBJECT_INFO_CONTENT, '')
      : `${pageReference}${property}`
          .replace(constants.CASE_INFO.CASE_INFO_CONTENT, '')
          .replace(constants.DATA_INFO.DATA_INFO_CONTENT, '');
    return FieldUtils.formatPageReference(target);
  }

  /**
   * [convertPageRefToInterestPage]
   * Description : Converts pageReference to clipboardpage notation
   * @param pageReference      pageReference
   * @returns pageReference to interestPage format
   *  @example <caption>Example for convertPageRefToInterestPage()</caption>
   * Example usage - Utils.convertPageRefToInterestPage('.Employees[4].Assets[5]')
   * returns - .Employees(5).Assets(6)
   */

  static convertPageRefToClipboardPageNotation(pageReference: string) {
    const regex = /\[\d+\]/g;
    return pageReference.replace(regex, (val) => {
      return val
        .replace(/\d+/g, (value) => `${parseInt(value, 10) + 1}`)
        .replace('[', '(')
        .replace(']', ')');
    });
  }

  /**
   * [convertClipboardPageToPageRefNotation]
   * Description : Converts clipboard page notation to pageReference notation
   * @param pageReference      pageReference
   * @returns pageReference format
   *  @example <caption>Example for convertClipboardPageToPageRefNotation()</caption>
   * Example usage - Utils.convertClipboardPageToPageRefNotation(' .Employees(5).Assets(6)')
   * returns - .Employees[4].Assets[5]
   */

  static convertClipboardPageToPageRefNotation(pageReference: string) {
    const regex = /\(\d+\)/g;
    return pageReference.replace(regex, (val) => {
      return val
        .replace(/\d+/g, (value) => `${parseInt(value, 10) - 1}`)
        .replace('(', '[')
        .replace(')', ']');
    });
  }

  static isString<T>(value: T) {
    return typeof value === 'string';
  }

  static rejectPendingActions(
    actionQueue: {
      type?: string;
      payload: {
        class: string;
        actionID: string;
        assignmentID: string;
        caseID: string;
        containerName: string;
        containerItemID: string;
        isCaseWideAction: boolean;
        doDeleteCaseSummary: boolean;
        isInCreateStage: boolean;
        businessID: string;
        isModalAction: true;
        outcome: string;
        context: string;
        pageReference: string;
        actionMgrID: string;
      };
    }[]
  ) {
    actionQueue.forEach(
      (action) => action.payload.actionMgrID && ActionManagerInstance.rejectActionByID(action.payload.actionMgrID)
    );
    return true;
  }

  /**
   * Checks whether the passed valueWithAnnotation has a valid annotation after extracting
   * the annotation from it.
   *
   * @param {string} valueWithAnnotation value with annotation to be checked
   */

  static isValidAnnotation(valueWithAnnotation: string) {
    const annotationSet = new Set([
      '@DATASOURCE',
      '@ACTIVE_CASES',
      '@ADDRESS',
      '@ASSOCIATED',
      '@ATTACHMENT',
      '@CASE',
      '@FILTERED_LIST',
      '@PARAGRAPH',
      '@P',
      '@ROUTING_INFO',
      '@USER',
      '@W',
      '@E',
      '@SHARED'
    ]);
    const firstSpaceIndex = valueWithAnnotation.indexOf(' ');
    const annotation = valueWithAnnotation.substring(0, firstSpaceIndex);
    return annotationSet.has(annotation);
  }

  static trimDotForProperty(key: string): string {
    if (typeof key === 'string' && key.startsWith('.')) {
      return key.substring(1);
    }
    return key;
  }

  static convertObjectWithStringToBoolean(features: { [key: string]: string | boolean | number }) {
    if (!features) {
      return undefined;
    }
    if (Array.isArray(features) || !(typeof features === 'object')) {
      return undefined;
    }
    Object.keys(features).forEach((key) => {
      let value;
      const propertyValue = features[key as keyof FeaturesFromInfinity];
      if (typeof propertyValue === 'string') {
        if (propertyValue === 'true') {
          value = true;
        } else if (propertyValue === 'false') {
          value = false;
        } else {
          value = propertyValue;
        }
      } else if (typeof propertyValue === 'object') {
        value = propertyValue;
        Utils.convertObjectWithStringToBoolean(propertyValue);
      } else {
        value = propertyValue;
      }
      features[key as keyof FeaturesFromInfinity] = value;
    });
    return features;
  }

  static rearrangeProperties(propertiesToReArrange: string[], clientData: NestedObject) {
    propertiesToReArrange.forEach((attachmentPath: string) => {
      let attachmentData = Utils.getIn(clientData, attachmentPath);
      if (!attachmentData) {
        return;
      }
      const idOldAttachment = Object.hasOwn(attachmentData, 'pyCount');
      if (idOldAttachment) {
        attachmentData = attachmentData.pxResults;
      }
      if (!attachmentData) {
        return;
      }
      const correctAttachments = attachmentData.filter((attachment: Record<string, string>) => !attachment.error);
      const errorAttachments = attachmentData.filter((attachment: Record<string, string>) => attachment.error);
      Utils.setIn(clientData, idOldAttachment ? `${attachmentPath}.pxResults` : attachmentPath, [
        ...correctAttachments,
        ...errorAttachments
      ]);
    });
  }

  /**
   *This API will return the etag associated with the assignment context
   * @param contextName - - name of the context
   * @returns
   */

  static getETag(contextName?: string | null) {
    if (!contextName) {
      return;
    }
    let context = contextName;
    const parts = context?.split('/') || [];
    const workareaIndex = parts.findIndex((part) => part.startsWith('workarea'));
    const modalIndex = parts.findIndex((part) => part.startsWith('modal'));
    if (workareaIndex !== -1) {
      context = parts.slice(0, workareaIndex + 1).join('/');
    } else if (modalIndex !== -1) {
      context = parts.slice(0, modalIndex + 1).join('/');
    }
    return getStore()?.getState()?.data[context]?.caseInfo?.headers?.etag;
  }

  private static readonly PAGELIST_PATH_REGEX = /^(?!.*\.\.)(?!\d)\w+(\.\w+)*\.\d+(\.\w+)+$/;

  /**
   * Checks if the given property path is a valid pagelist property.
   * A valid pagelist property contains one or more digits between dots.
   *
   * @param {string} property - The property path to check.
   * @returns {boolean} - Returns true if the property path is a valid pagelist property, otherwise false.
   *
   * @example
   * // returns true
   * isValidPageListProperty('caseInfo.content.Employee.1.Name');
   */
  static isValidPageListProperty(property: string): boolean {
    return Utils.PAGELIST_PATH_REGEX.test(property);
  }

  static generateDataPageHashWithParameters(
    parameters: Record<string, string>,
    dataPageName: string,
    c11nEnv: C11nEnv
  ) {
    const parameterValues: string[] = [];
    let hashDigest = '';
    const pageReference = c11nEnv.getPageReference();
    const currentContextName = c11nEnv.getContextName();
    Object.keys(parameters).forEach((key: string) => {
      const parameterValue = parameters[key];
      let resolvedValue = AnnotationProcessor.isProperty(parameterValue)
        ? StateManager.getValue(
            `.${AnnotationProcessor.getPropertyName(parameterValue)}`,
            pageReference,
            currentContextName
          )
        : parameterValue;
      if (resolvedValue === undefined || resolvedValue === null || resolvedValue === '') {
        resolvedValue = constants.NULL_STRING;
      }
      parameterValues.push(resolvedValue);
    });

    let dataPageWithHash = dataPageName;
    const parameterWithHash = parameterValues.join(constants.HASH_DELIMITER);
    if (parameterWithHash) {
      hashDigest = Utils.getHashedString(parameterWithHash);
      dataPageWithHash = dataPageName + constants.UNDERSCORE_STRING + hashDigest;
    }
    return dataPageWithHash;
  }

  /**
   * Parses a Pega DateTime string in the format 'YYYYMMDDTHHmmss.SSS' (optionally ending with ' GMT')
   * and returns the corresponding timestamp in milliseconds.
   *
   * @example <caption>Example for parsePegaDateTime()</caption>
   * // Returns a timestamp for the given Pega DateTime string
   * const timestamp = AssetLoader.parsePegaDateTime('20240623T153045.123 GMT');
   *
   * @param {string | null | undefined} dateTimeStr - The Pega DateTime string to parse.
   * @returns {number | null} The timestamp in milliseconds, or null if parsing fails.
   * @function
   */
  static parsePegaDateTimeToTimestamp(dateTimeStr: string | null | undefined): number | null {
    if (!dateTimeStr) return null;

    // Match the expected format: YYYYMMDDTHHmmss.SSS
    const match = dateTimeStr.replace(' GMT', '').match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})\.(\d{3})$/);
    if (!match) return null;

    const [, year, month, day, hour, minute, second, ms] = (match as string[]).map(Number);

    const date = new Date(year, month - 1, day, hour, minute, second, ms);
    return date.getTime();
  }

  static normalizePropertyRef(str: string): string {
    const prefix = 'caseInfo.content.';
    let result = str.startsWith(prefix) ? str.substring(prefix.length) : str;
    result = result.replace(/\[\d+\]/g, '');
    return result.startsWith('.') ? result.substring(1) : result;
  }

  /**
   * Resolves the parent page class for a nested property path.
   * Used internally to traverse property segments and determine the classID for the last prop.
   * @private
   * @param propName - The property path (e.g. "Employee.Address.Street").
   * @returns The resolved pageClass string, or undefined if not found.
   */
  static resolvePageClass(fullRef: string, baseClassID: string): string | undefined {
    if (!baseClassID) return;
    fullRef = this.normalizePropertyRef(fullRef);
    const properties = fullRef.split('.').slice(0, -1);
    let classID = baseClassID;
    for (const prop of properties) {
      const fieldMetadata = getPropertyMetadata(prop, classID);
      if (!fieldMetadata) return;
      if (Array.isArray(fieldMetadata) && fieldMetadata[0]?.pageClass) {
        classID = fieldMetadata[0].pageClass;
      } else if (!Array.isArray(fieldMetadata) && fieldMetadata.pageClass) {
        classID = fieldMetadata.pageClass;
      }
      if (!classID) return;
    }
    return classID;
  }
}

export default Utils;
